import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../../lib/supabaseClient';
import { borrarCierre, listFilasDeCierre, listSkusDeImpulso } from '../../api/impulso';
import { skusVigentesEn } from '../../domain/catalogoDeCierre';
import type { CierreEnBandeja, FilaDeCierre, SkuDeImpulso } from '../../api/impulso';
import {
  COMPETENCIA, ESTADO_DE_CIERRE_LABEL, EXHIBICION, INCIDENCIA, OBJECION, QUIEBRE,
  esDeudaDeCierre,
} from '../../domain/impulso';
import type { EstadoDeCierre } from '../../domain/impulso';
import type { CampaignOption } from '../../api/campaigns';
import { coincideTodasLasPalabras, rangoDelMes } from '../../domain/tableFilters';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';
import CierreDeTurnoModal from './CierreDeTurnoModal';
import {
  BotonDetalle, Dato, FILA_ABIERTA, PanelDeDetalle, Seccion, Senal,
} from '../../components/DetalleExpandible';
import BorrarRegistro from '../../components/BorrarRegistro';
import TarjetasResumen from '../../components/TarjetasResumen';

const TZ = 'America/Lima';
const hoyLima = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

/** Traduce el valor guardado a lo que se leyó en el celular. */
function etiqueta(lista: readonly { valor: string; etiqueta: string }[], valor: string): string {
  return lista.find((o) => o.valor === valor)?.etiqueta ?? valor;
}

/**
 * Un valor que no aporta: «No faltó nada», «Nada, todo normal», «—».
 *
 * Existe porque en la rejilla anterior un campo vacío pesaba exactamente lo mismo que un dato real,
 * y siete pares con el mismo peso no tienen jerarquía: hay que leerlos todos para descubrir cuál
 * dice algo. Es `gray-500` y no `gray-400` a propósito — `gray-400` sobre blanco no llega a 4.5:1 y
 * esto sigue siendo texto que alguien puede necesitar leer, no un adorno.
 */
function Neutro({ children }: { children: ReactNode }) {
  return <span className="text-gray-500">{children}</span>;
}

/**
 * Un texto que escribió la impulsadora, con su rótulo.
 *
 * No es un `Dato` porque es prosa y no un valor: necesita superficie propia para que se vea dónde
 * empieza y dónde termina. Los dos comentarios del formulario —el de la tienda y el de la
 * competencia— usan el MISMO tratamiento: antes el de competencia llevaba borde y rótulo indigo, y
 * era el único énfasis de color del panel entero puesto en el campo menos importante de los dos.
 */
function TextoAbierto({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="mt-3">
      <p className="text-xs text-gray-500">{rotulo}</p>
      <p className="mt-0.5 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-900">
        {children}
      </p>
    </div>
  );
}

/**
 * Una fila «producto → unidades» dentro de una línea.
 *
 * El número va a la derecha y en `tabular-nums`: las unidades se comparan en vertical, y con cifras
 * de ancho variable «12» y «7» no caen alineadas aunque las dos estén alineadas a la derecha. Sin
 * eso la columna numérica no se puede barrer de un vistazo, que es justo para lo que sirve.
 */
function LineaDeProducto({ nombre, unidades }: { nombre: string; unidades: number }) {
  const cero = unidades === 0;
  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-1 last:border-b-0">
      <span className={`text-sm ${cero ? 'text-gray-500' : 'text-gray-900'}`}>{nombre}</span>
      <span className={`text-sm tabular-nums ${cero ? 'text-gray-500' : 'font-semibold text-gray-900'}`}>
        {unidades}
      </span>
    </li>
  );
}

/**
 * Lo vendido en el turno, por producto y agrupado por línea.
 *
 * Reemplaza a `porSku.map(s => `${s.sku}: ${s.unidades}`).join(' · ')`, que imprimía los códigos
 * crudos —`rap_00`, `tos_clasicas`— pegados en un solo renglón. El usuario lo describió así:
 * *«se ve feo, abreviado y todo en una fila; no se entiende bien a simple vista»*.
 *
 * Tres decisiones, cada una arreglando una cosa distinta:
 *
 *  1. **El catálogo de la campaña** (`impulso_skus`) trae el nombre legible y la línea. Sin él solo
 *     hay códigos, y un código no es un nombre de producto para nadie que no programó la tabla.
 *  2. **Se muestra el catálogo COMPLETO, no solo lo vendido.** La API filtra `unidades > 0` al
 *     enviar, así que un producto sin fila podía ser «no vendió Linaza» o «Linaza no existe en esta
 *     campaña» y las dos cosas se veían igual: no se veían. Ahora el cero se ve y se lee como cero.
 *  3. **Dos columnas, una por línea.** Son dos listas de largo distinto —5 y 3 productos—, no una
 *     rejilla: cada columna corre sola y no se fuerzan filas parejas entre una y otra.
 */
function VendidoPorProducto({ fila, catalogo }: { fila: CierreEnBandeja; catalogo: SkuDeImpulso[] }) {
  const vendido = new Map(fila.porSku.map((s) => [s.sku, s.unidades]));

  if (catalogo.length === 0) {
    // Campaña sin catálogo cargado: queda el código crudo, no hay de dónde sacar el nombre. Pero
    // sigue siendo una lista con su columna numérica; volver al renglón de códigos concatenados
    // sería reponer el problema por el que existe este componente.
    if (fila.porSku.length === 0) return <p className="text-sm text-gray-500">No reportó ventas.</p>;
    return (
      <ul className="grid gap-x-8 sm:grid-cols-2">
        {fila.porSku.map((s) => (
          <LineaDeProducto key={s.sku} nombre={s.sku} unidades={s.unidades} />
        ))}
      </ul>
    );
  }

  // El catálogo llega ordenado por `sort`, así que agrupar en orden de aparición conserva tanto el
  // orden de las líneas como el de los productos dentro de cada una.
  const lineas: { nombre: string; skus: SkuDeImpulso[] }[] = [];
  for (const s of catalogo) {
    const grupo = lineas.find((l) => l.nombre === s.linea);
    if (grupo) grupo.skus.push(s);
    else lineas.push({ nombre: s.linea, skus: [s] });
  }

  return (
    <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
      {lineas.map((l) => {
        const subtotal = l.skus.reduce((s, k) => s + (vendido.get(k.code) ?? 0), 0);
        return (
          <div key={l.nombre}>
            <p className="mb-1 text-sm font-semibold text-gray-900">{l.nombre}</p>
            <ul>
              {l.skus.map((k) => (
                <LineaDeProducto key={k.code} nombre={k.label} unidades={vendido.get(k.code) ?? 0} />
              ))}
            </ul>
            <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-gray-300 pt-1">
              <span className="text-xs text-gray-600">Subtotal {l.nombre}</span>
              <span className="text-sm font-semibold tabular-nums text-gray-900">{subtotal}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * El nombre legible de un SKU, con su línea delante.
 *
 * La línea no sobra: «Clásicas» e «Integrales» y «Cero Cero» existen en Rapiditas **y** en Tostadas.
 * Una lista de lo que faltó en góndola que diga solo «Clásicas» no dice cuál de las dos faltó.
 */
function nombreDeSku(catalogo: SkuDeImpulso[], code: string): string {
  const s = catalogo.find((x) => x.code === code);
  return s ? `${s.linea} · ${s.label}` : code;
}

/**
 * Bandeja de CIERRES DE TURNO — la dinamica de impulso.
 *
 * Vive dentro de la seccion de Registros de campo porque el usuario la penso asi: *«deberia ir ahi,
 * porque tenemos tambien la categoria de dinamica»*. El canje es una dinamica y esta es otra.
 *
 * ---------------------------------------------------------------------------
 * EL CAMBIO DE EJE (0099, 8-set): la bandeja se maneja por POSICIONES
 * ---------------------------------------------------------------------------
 * Nacio leyendo `impulso_reports`, y por eso solo podia mostrar los cierres que EXISTEN. El usuario
 * pidio lo contrario: *«ver de los asientos asignados quienes faltan llenar cierres de turno y
 * poder completar registros manualmente o editarlos»*, con el formato de la bandeja de validacion.
 * Una fila que no existe no se lista, asi que habia que partir de los turnos asignados y enganchar
 * el cierre encima — que es lo que ya hace `Validacion.tsx`.
 *
 * Con los numeros de Bimbo del 8-set: 70 posiciones con asignacion viva y 52 con cierre. Las 18 que
 * faltaban no se podian ver desde ninguna pantalla.
 *
 * ⚠️ **La campania pasa a ser OBLIGATORIA** —antes habia un «Todas»—. No es un recorte gratuito:
 * enumerar las posiciones asignadas de todas las campanias son miles de filas (P&G sola tiene
 * 3.344) y el cierre de turno solo existe donde hay catalogo de productos. Sin campania, la
 * pregunta «¿quien no lleno su cierre?» no tiene respuesta bien definida. El selector solo ofrece
 * campanias CON `impulso_skus`.
 *
 * ⚠️ **La lista completa sin estado MIENTE, y ya mintio una vez**: en el tablero del cliente los
 * turnos en curso se pintaban como «sin registro». Por eso cada fila lleva su estado y **solo
 * `falta_cierre` cuenta como deuda** (`domain/impulso.ts`, con pruebas caso por caso).
 */

/** El color de cada estado. Solo la deuda es roja: si todo grita, nada avisa. */
const ESTADO_CLS: Record<EstadoDeCierre, string> = {
  con_cierre: 'bg-green-100 text-green-800',
  falta_cierre: 'bg-red-100 text-red-800',
  en_curso: 'bg-blue-100 text-blue-800',
  sin_marca: 'bg-amber-100 text-amber-800',
  ausencia: 'bg-gray-100 text-gray-700',
  futuro: 'bg-gray-50 text-gray-500',
  sin_asignar: 'bg-gray-50 text-gray-500',
};

/**
 * ¿Se le puede completar el cierre a mano?
 *
 * **La regla es una sola: hay asignacion y todavia no hay cierre.** Nada mas.
 *
 * Arranco mas restrictiva —solo `falta_cierre` y `sin_marca`— con el argumento de que una ausencia
 * no tiene turno que cerrar y un turno en curso todavia no termino. El usuario lo corrigio mirando
 * la pantalla: *«incluir en estas situaciones boton para agregar registro»*, señalando justamente
 * una fila en «Ausencia».
 *
 * Y tiene razon, porque mi argumento confundia el DATO con la REALIDAD. «Ausencia» sale de una
 * autorizacion aprobada para esa fecha: puede ser un permiso de medio dia, puede estar mal cargada,
 * o la persona puede haber ido a trabajar igual. El sistema no sabe cual de las tres; el
 * coordinador si. Una pantalla que le niega la accion porque su propio dato dice otra cosa lo
 * obliga a arreglar el dato para poder registrar lo que paso, que es el orden inverso.
 *
 * Lo que SI queda fuera es `sin_asignar`, y no por criterio: **`impulso_reports.worker_id` es NOT
 * NULL**. Un cierre es lo que reporto una persona, asi que sin recurso asignado el registro no puede
 * existir. Dejar el boton ahi seria una trampa —la RPC lo rechaza con «Falta la asignacion»—, asi
 * que en su lugar la fila explica por que no lo tiene.
 *
 * Cuando el estado NO es `falta_cierre`, el modal abre con un aviso diciendo lo que el sistema cree
 * de ese turno. La accion no se bloquea; se informa.
 */
function sePuedeCompletar(f: FilaDeCierre): boolean {
  return f.assignmentId !== null && f.cierre === null;
}

/**
 * Quién puede accionar en esta bandeja: «Agregar registro», «Editar» y «Borrar».
 *
 * 🔴 **No es la misma regla que la bandeja de canjes, aunque viven en la misma pantalla.** Las tres
 * RPC de acá —`registrar_cierre_manual`, `corregir_cierre` y `borrar_cierre`, de la 0099 y la
 * 0101— se apoyan en `puede_editar_asignacion`, que es `admin OR coordinador DESIGNADO de la
 * campaña OR fila en campaign_staff`. **No tiene rama para `datos`**, mientras que sus hermanas
 * `puede_editar_canje` y `puede_editar_visita` empiezan las dos con `admin or datos`.
 *
 * Esa inconsistencia se descubrió el 10-set verificando contra la base, y hasta entonces la
 * pantalla le dibujaba al validador los tres botones y el servidor se los rechazaba: la trampa que
 * la casa prohíbe por escrito («un botón que el servidor va a rechazar es una trampa», BITACORA
 * §2). Acá se cierra el lado de la pantalla, que es el lado seguro.
 *
 * ⚠️ Queda un hueco del MISMO tipo y de antes, sin arreglar acá porque necesita un dato que el
 * frontend no tiene: un supervisor con rol `coordinador` que no sea el designado NI esté en
 * `campaign_staff` de esa campaña también ve los botones y también le rebotan. Para cerrarlo hace
 * falta una consulta de «campañas donde soy equipo», que hoy no existe (solo hay
 * `campaniasQueCoordino`, que mira `coordinator_id`). Anotado en PENDIENTES.
 */
export function puedeAccionarCierre(roles: string[]): boolean {
  if (!puedeEditar('canjes', roles)) return false;
  return roles.includes('admin') || roles.includes('coordinador');
}

export default function CierresDeTurnoBandeja() {
  const { roles } = useAuth();
  const puedeAccionar = puedeAccionarCierre(roles);

  const [desde, setDesde] = useState(hoyLima());
  const [hasta, setHasta] = useState(hoyLima());
  const [campanias, setCampanias] = useState<CampaignOption[]>([]);
  const [campania, setCampania] = useState('');
  const [filas, setFilas] = useState<FilaDeCierre[]>([]);
  const [huerfanos, setHuerfanos] = useState<CierreEnBandeja[]>([]);
  const [catalogo, setCatalogo] = useState<SkuDeImpulso[]>([]);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  /** Mes elegido (`yyyy-MM`). No filtra por su cuenta: fija el rango y la carga se dispara. */
  const [mes, setMes] = useState('');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [modal, setModal] = useState<FilaDeCierre | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  // Solo campanias CON catalogo de impulso: en una de canje esta solapa no tiene nada que recoger.
  useEffect(() => {
    void (async () => {
      const [camps, skus] = await Promise.all([
        supabase.from('campaigns').select('id, client, brand')
          .is('archived_at', null).eq('es_banco_pruebas', false).order('client'),
        supabase.from('impulso_skus').select('campaign_id').eq('active', true),
      ]);
      const conImpulso = new Set(
        ((skus.data ?? []) as { campaign_id: string }[]).map((r) => r.campaign_id),
      );
      const lista = ((camps.data ?? []) as CampaignOption[]).filter((c) => conImpulso.has(c.id));
      setCampanias(lista);
      // Se elige la primera sola: una pantalla que arranca vacia esperando que elijas algo obvio
      // es una pantalla que suma un clic a cada visita.
      setCampania((actual) => (actual === '' && lista.length > 0 ? lista[0].id : actual));
    })();
  }, []);

  const cargar = useCallback(async () => {
    if (campania === '') { setFilas([]); setHuerfanos([]); return; }
    setCargando(true);
    setError('');
    try {
      const [bandeja, cat] = await Promise.all([
        listFilasDeCierre(campania, desde, hasta),
        // Un catalogo que no carga deja `[]`, y con `[]` el panel cae al modo sin catalogo en vez
        // de romper la bandeja entera: lo demas del cierre se sigue leyendo.
        listSkusDeImpulso(campania).catch(() => [] as SkuDeImpulso[]),
      ]);
      setFilas(bandeja.filas);
      setHuerfanos(bandeja.huerfanos);
      setCatalogo(cat);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los cierres.');
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, campania]);

  useEffect(() => { void cargar(); }, [cargar]);

  /**
   * Elegir un mes fija el rango, y `cargar` se vuelve a disparar solo porque depende de las fechas.
   *
   * Es el mismo atajo de Validacion y comparte su implementacion (`rangoDelMes`): elegir «setiembre»
   * es un clic contra dos calendarios, y es como se mira esto en la practica —el mes cerrado, no una
   * ventana arbitraria—.
   */
  function elegirMes(valor: string) {
    setMes(valor);
    const r = rangoDelMes(valor);
    if (!r) return;
    setDesde(r.desde);
    setHasta(r.hasta);
  }

  const pendientes = useMemo(() => filas.filter((f) => esDeudaDeCierre(f.estado)).length, [filas]);
  /*
   * Los de EJEMPLO quedan FUERA de los tres contadores, y es la correccion de una incoherencia que
   * el usuario vio antes que yo: la bandeja sumaba 8365 unidades y el tablero del cliente 8322,
   * exactamente los 43 de los dos registros sembrados. Un mismo dato con dos totales segun la
   * pantalla es peor que un dato faltante, porque las dos cifras parecen creibles.
   */
  const reales = useMemo(() => filas.filter((f) => f.cierre !== null && !f.cierre.esEjemplo),
    [filas]);
  const ejemplos = useMemo(() => filas.filter((f) => f.cierre?.esEjemplo).length, [filas]);
  const unidades = reales.reduce((s, f) => s + (f.cierre?.unidades ?? 0), 0);
  /**
   * Lo que se ve. Los dos filtros se acumulan con Y, que es lo que la gente espera de una barra asi.
   *
   * El texto se busca en nombre, documento y tienda —lo que el coordinador tiene a mano cuando
   * alguien le escribe—, con el MISMO motor que la casilla de Validacion: sin tildes, y pidiendo
   * que cada palabra este en alguna parte. Por eso «flor surco» encuentra a Flor en Makro Surco y
   * no a todas las Flor, y «huamani» encuentra a «Huamani» con tilde.
   */
  const visibles = useMemo(() => filas.filter((f) => {
    if (soloPendientes && !esDeudaDeCierre(f.estado)) return false;
    return coincideTodasLasPalabras(busqueda, f.trabajador, f.documento, f.tienda);
  }), [filas, soloPendientes, busqueda]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="cdt-desde">Desde</label>
          <input id="cdt-desde" type="date" value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="cdt-hasta">Hasta</label>
          <input id="cdt-hasta" type="date" value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="cdt-camp">Campaña</label>
          <select id="cdt-camp" value={campania} onChange={(e) => setCampania(e.target.value)}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-sm">
            {campanias.length === 0 && <option value="">Sin campañas de impulso</option>}
            {campanias.map((c) => (
              <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={() => void cargar()}
          className="rounded border border-gray-300 px-3 py-1 text-sm">
          Actualizar
        </button>
      </div>

      {/* La barra de filtros, separada de la de busqueda a proposito: la de arriba decide QUE se
          trae del servidor y la de abajo recorta lo YA traido, sin esperar nada. Mezclarlas hace
          que dos controles que se ven iguales se comporten distinto —uno instantaneo y el otro con
          espera—, que es la queja que ya resolvio la tabla de Asignaciones (`tableFilters.ts`). */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">
          Mes
          <input type="month" value={mes} onChange={(e) => elegirMes(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1" />
        </label>
        <label className="flex flex-col text-sm">
          Nombre, tienda o documento
          <input type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Ej.: Huamani, Surco, 46053436"
            className="w-64 rounded border border-gray-300 px-2 py-1" />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={soloPendientes}
            onChange={(e) => setSoloPendientes(e.target.checked)} />
          Solo los que faltan
        </label>
        {/* El contador de lo filtrado, y solo cuando hay filtro puesto: sin esto, filtrar y no
            encontrar nada se ve igual que un rango sin turnos, y son dos problemas distintos. */}
        {(busqueda !== '' || soloPendientes) && (
          <span className="text-sm text-gray-600">
            {/* «Mostrando» y no «N de M turnos»: la tarjeta de arriba ya dice «2 de 7 turnos» con
                OTRO significado —cuantos tienen cierre—, y dos contadores con la misma frase y
                numeros distintos es exactamente como se lee mal una pantalla. Lo cazo una prueba. */}
            Mostrando {visibles.length} de {filas.length}
          </span>
        )}
      </div>

      {/* Los numeros que decide mirar alguien que entra a reclamar: cuanto falta, cuanto hay, y
          cuanto se reporto. El pendiente va primero porque es el unico sobre el que se actua.

          Antes eran tres tarjetas escritas a mano acá, con otra tipografia que las de «Hoy»: ahora
          es el componente compartido, que es lo que pidio el usuario cuando dijo *«mismos tipos de
          tarjetas»*. «Cierres registrados» perdio el «2 de 7 turnos» de adentro del numero grande y
          lo bajo al detalle, donde se lee. */}
      <TarjetasResumen indicadores={[
        { titulo: 'Cierres que faltan', valor: pendientes, tono: 'malo' },
        { titulo: 'Cierres registrados', valor: reales.length,
          detalle: `de ${filas.length} turnos` },
        { titulo: 'Unidades reportadas', valor: unidades },
      ]} />

      {error && <p className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {cargando && <p className="text-sm text-gray-500">Cargando…</p>}
      {!cargando && filas.length === 0 && campania !== '' && (
        <p className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-600">
          No hay turnos asignados de esa campaña en ese rango.
        </p>
      )}
      {/* Hay turnos pero el filtro los dejo todos afuera. Decirlo evita que alguien concluya que
          el rango esta vacio y se vaya a cambiar las fechas cuando el problema es lo que escribio. */}
      {!cargando && filas.length > 0 && visibles.length === 0 && (
        <p className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Ninguno de los {filas.length} turnos del rango coincide con el filtro.{' '}
          <button type="button" onClick={() => { setBusqueda(''); setSoloPendientes(false); }}
            className="text-blue-700 underline">
            Limpiar el filtro
          </button>
        </p>
      )}

      {/* Los registros sembrados existen en la base de una campania de PRODUCCION. Decirlo en la
          pantalla es lo que hace que alguien los borre; escondidos, se quedan para siempre. */}
      {ejemplos > 0 && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <strong>
            {ejemplos === 1 ? 'Hay 1 registro de EJEMPLO' : `Hay ${ejemplos} registros de EJEMPLO`}
          </strong>{' '}
          en este rango: datos sembrados para armar pantallas, no operación. No cuentan en los
          totales de arriba ni en el tablero del cliente, y conviene borrarlos de la base.
        </div>
      )}

      {visibles.length > 0 && (
        // La tabla es BLANCA y encerrada, como la de canjes. No es cosmetica: el panel de detalle
        // era `bg-gray-50` sobre un shell de panel que tambien es `bg-gray-50`, asi que el unico
        // resaltado que existia tenia contraste cero.
        <div className="overflow-x-auto rounded border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Impulsadora</th>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Unidades</th>
                <th className="px-3 py-2">Señales</th>
                <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((fila) => {
                const f = fila.cierre;
                const abiertoAqui = abierta === fila.key;
                return (
                  <Fragment key={fila.key}>
                    <tr className={`border-b border-gray-100 align-top ${abiertoAqui ? FILA_ABIERTA : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">
                        {fila.fecha}
                        <span className="block text-xs text-gray-500">{fila.horario}</span>
                      </td>
                      <td className="px-3 py-2">
                        {fila.trabajador}
                        <span className="block text-xs text-gray-500">{fila.documento}</span>
                      </td>
                      <td className="px-3 py-2">{fila.tienda}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${ESTADO_CLS[fila.estado]}`}>
                          {ESTADO_DE_CIERRE_LABEL[fila.estado]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">
                        {f ? f.unidades : <span className="font-normal text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {/* «Completado en oficina» va PRIMERO y en gris grave: es lo que le
                              permite a cualquiera que lea esta fila saber que el numero no lo dijo
                              la impulsadora. Sin esa distincion, el rotulo del tablero del cliente
                              —«segun el cierre de turno»— seria falso. */}
                          {/* Primero de todo: cambia como se lee el resto de la fila. */}
                          {f?.esEjemplo && <Senal tono="grave">ejemplo · no cuenta</Senal>}
                          {f?.origen === 'oficina' && <Senal tono="neutro">completado en oficina</Senal>}
                          {fila.correcciones > 0 && (
                            <Senal tono="neutro">
                              editado{fila.correcciones > 1 ? ` ×${fila.correcciones}` : ''}
                            </Senal>
                          )}
                          {f?.registroTardio && <Senal>tardío</Senal>}
                          {f?.sinAsistencia && <Senal tono="grave">sin marca</Senal>}
                          {f?.fueraDeProgramacion && <Senal tono="grave">sin turno</Senal>}
                          {f && f.quiebre !== 'ninguno' && <Senal>quiebre</Senal>}
                          {f && f.objeciones.length > 0 && (
                            <Senal>
                              {f.objeciones.length} {f.objeciones.length === 1 ? 'objeción' : 'objeciones'}
                            </Senal>
                          )}
                          {f && f.incidencia !== 'ninguna' && <Senal>incidencia</Senal>}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {puedeAccionar && sePuedeCompletar(fila) && (
                          <button type="button" onClick={() => setModal(fila)}
                            className="rounded bg-blue-700 px-2 py-1 text-xs font-medium text-white hover:bg-blue-800">
                            Agregar registro
                          </button>
                        )}
                        {/* Sin recurso asignado NO se puede: `impulso_reports.worker_id` es NOT
                            NULL, un cierre es lo que reporto una persona. Se dice en la fila en
                            lugar de dejar un hueco que se lee como un boton que falta. */}
                        {puedeAccionar && fila.assignmentId === null && fila.cierre === null && (
                          <span className="text-xs text-gray-500"
                            title="Un cierre necesita a la persona que lo reporta. Asigna el turno en Asignaciones y después vuelve acá.">
                            Falta asignar el turno
                          </span>
                        )}
                        {puedeAccionar && f && (
                          <button type="button" onClick={() => setModal(fila)}
                            className="mr-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                            Editar
                          </button>
                        )}
                        {puedeAccionar && f && (
                          <span className="mr-1 inline-block">
                            <BorrarRegistro
                              sujeto={`Cierre de ${fila.trabajador} · ${fila.tienda} · ${fila.fecha}`}
                              queSePierde="sus unidades por producto y sus objeciones"
                              onBorrar={(motivo) => borrarCierre(f.id, motivo)}
                              /* Al borrarlo, la impulsadora puede volver a registrar el suyo desde
                                 el celular: el formulario movil se bloquea por persona + campania
                                 + fecha, asi que mientras este cierre exista el de ella no entra. */
                              onBorrado={() => void cargar()}
                            />
                          </span>
                        )}
                        {f && (
                          <BotonDetalle
                            abierto={abiertoAqui}
                            onClick={() => setAbierta(abiertoAqui ? null : fila.key)}
                          />
                        )}
                      </td>
                    </tr>
                    {abiertoAqui && f && (
                      <tr className="border-b border-gray-200">
                        {/* `p-0`: el panel trae su propio canal gris y su propia sangria, y es esa
                            sangria la que lo hace ver colgando de su fila. */}
                        <td colSpan={7} className="p-0">
                          <PanelDeDetalle
                            titulo="Detalle del cierre"
                            sujeto={`${fila.tienda} · ${fila.trabajador} · ${fila.fecha}`}
                            onCerrar={() => setAbierta(null)}
                          >
                            {/* La procedencia va ARRIBA y no al pie cuando el cierre lo completo la
                                oficina: es lo primero que cambia como se lee todo lo de abajo. */}
                            {f.origen === 'oficina' && (
                              <div className="rounded border border-amber-200 bg-amber-50 p-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                                  Completado por la oficina
                                </p>
                                <p className="mt-1 text-sm text-amber-900">
                                  {f.notaOficina ?? 'Sin motivo registrado.'}
                                </p>
                              </div>
                            )}
                            <Seccion
                              titulo="Vendido por producto"
                              extra={(
                                <span className="text-xs text-gray-500">
                                  Total del turno{' '}
                                  <span className="text-sm font-bold tabular-nums text-gray-900">
                                    {f.unidades}
                                  </span>
                                </span>
                              )}
                            >
                              {/* Con los productos que se preguntaban ese día: el catálogo cambió el 23-oct (0323). */}
                              <VendidoPorProducto fila={f} catalogo={skusVigentesEn(catalogo, fila.fecha)} />
                            </Seccion>

                            <Seccion titulo="Ejecución en tienda">
                              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                                <Dato etiqueta="Faltó en góndola">
                                  {f.quiebre === 'ninguno'
                                    ? <Neutro>{etiqueta(QUIEBRE, f.quiebre)}</Neutro>
                                    : etiqueta(QUIEBRE, f.quiebre)}
                                </Dato>
                                {f.quiebreSkus.length > 0 && (
                                  <Dato etiqueta="Productos que faltaron">
                                    <ul className="mt-0.5 flex flex-wrap gap-1">
                                      {f.quiebreSkus.map((c) => (
                                        <li key={c}><Senal>{nombreDeSku(catalogo, c)}</Senal></li>
                                      ))}
                                    </ul>
                                  </Dato>
                                )}
                                <Dato etiqueta="Exhibición">
                                  {f.exhibicion === 'no'
                                    ? <Neutro>{etiqueta(EXHIBICION, f.exhibicion)}</Neutro>
                                    : etiqueta(EXHIBICION, f.exhibicion)}
                                </Dato>
                                <Dato etiqueta="Nuestro precio">
                                  {f.precioReferencia === null
                                    ? <Neutro>No lo anotó</Neutro>
                                    : `S/ ${f.precioReferencia}`}
                                </Dato>
                              </dl>
                              {f.ejecucionComentario && (
                                <TextoAbierto rotulo="Comentario de la tienda">
                                  {f.ejecucionComentario}
                                </TextoAbierto>
                              )}
                            </Seccion>

                            <Seccion titulo="Competencia">
                              {/* Nuestro precio se REPITE aca, pegado al ajeno: comparar dos
                                  precios en diagonal es justo lo unico que alguien quiere hacer
                                  con este bloque. */}
                              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                                <Dato etiqueta="Nuestro precio">
                                  {f.precioReferencia === null
                                    ? <Neutro>No lo anotó</Neutro>
                                    : `S/ ${f.precioReferencia}`}
                                </Dato>
                                <Dato etiqueta="Precio de la competencia">
                                  {f.competenciaPrecio === null
                                    ? <Neutro>No lo anotó</Neutro>
                                    : `S/ ${f.competenciaPrecio}`}
                                </Dato>
                                <Dato etiqueta="Impulsadora de otra marca">
                                  {f.competencia === 'no'
                                    ? <Neutro>{etiqueta(COMPETENCIA, f.competencia)}</Neutro>
                                    : etiqueta(COMPETENCIA, f.competencia)
                                      + (f.competenciaCual ? ` (${f.competenciaCual})` : '')}
                                </Dato>
                                <Dato etiqueta="Su exhibición">
                                  {f.competenciaExhibicion
                                    ? f.competenciaExhibicion
                                    : <Neutro>No la describió</Neutro>}
                                </Dato>
                              </dl>
                              {f.competenciaComentario && (
                                <TextoAbierto rotulo="Lo que observó de la competencia">
                                  {f.competenciaComentario}
                                </TextoAbierto>
                              )}
                            </Seccion>

                            <Seccion titulo="Lo que dijeron los que no compraron">
                              {f.objeciones.length === 0 ? (
                                <p className="text-sm text-gray-500">Ninguna objeción en particular.</p>
                              ) : (
                                <ul className="space-y-1">
                                  {f.objeciones.map((o) => (
                                    <li key={o.valor} className="rounded border border-gray-200 bg-gray-50 p-2">
                                      <span className="text-sm font-semibold text-gray-900">
                                        {etiqueta(OBJECION, o.valor)}
                                      </span>
                                      {o.comentario && (
                                        <span className="block text-sm text-gray-700">{o.comentario}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </Seccion>

                            <Seccion titulo="Incidencias">
                              {f.incidencia === 'ninguna' ? (
                                <p className="text-sm text-gray-500">
                                  {etiqueta(INCIDENCIA, f.incidencia)}.
                                </p>
                              ) : (
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {etiqueta(INCIDENCIA, f.incidencia)}
                                  </p>
                                  {f.incidenciaDetalle && (
                                    <p className="text-sm text-gray-700">{f.incidenciaDetalle}</p>
                                  )}
                                </div>
                              )}
                            </Seccion>

                            <p className="border-t border-gray-200 pt-3 text-xs text-gray-500">
                              Registrado el{' '}
                              {formatInTimeZone(new Date(f.registradoEn), TZ, 'dd/MM/yyyy HH:mm')}
                              {' · '}{f.campania}
                            </p>
                          </PanelDeDetalle>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Los cierres que no cuelgan de ninguna posicion del rango.
          NO se descartan: al 8-set hay 3 asi en produccion y uno no tiene posicion que lo explique.
          Una bandeja que dice ser la lista completa y tira filas en silencio es peor que la que
          habia, porque nadie se entera de que faltan. */}
      {huerfanos.length > 0 && (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">
            {huerfanos.length === 1
              ? '1 cierre sin posición asignada'
              : `${huerfanos.length} cierres sin posición asignada`}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Existen en la base pero no se pudieron enganchar a ningún turno de este rango. Cuentan en
            el tablero del cliente, así que se listan acá en lugar de desaparecer.
          </p>
          <ul className="mt-2 space-y-1">
            {huerfanos.map((h) => (
              <li key={h.id} className="text-sm text-amber-900">
                {h.fecha} · {h.trabajador} · {h.tienda} · {h.unidades} unidades
                {h.origen === 'oficina' && ' · completado en oficina'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {modal && (
        <CierreDeTurnoModal
          fila={modal}
          skus={skusVigentesEn(catalogo, modal.fecha)}
          marca={campanias.find((c) => c.id === campania)?.brand ?? null}
          cliente={campanias.find((c) => c.id === campania)?.client ?? null}
          onCerrar={() => setModal(null)}
          onGuardado={() => { setModal(null); void cargar(); }}
        />
      )}
    </div>
  );
}
